/**
 * The cockpit's pieces: who, what kind of record, how a number is trending, whether a week is on
 * pace, whether a call may be made now. Props in, nothing fetched. Every colour here sits beside
 * a word; none of them is the only way a reading is given.
 */
import { languageLabel, localCallWindow, type ConsentType, type Language } from "@tlcrm/shared";
import type { ReactNode } from "react";

import type { Pace } from "../lib/cockpit";

import { initials } from "../lib/format";
import { ErrorState } from "./ui";

/* ---- People and records -------------------------------------------------------------------- */

/** A person's initials, square. The signed-in person's own is the only one tinted. */
export function Avatar({
  name,
  you,
  size = "md",
}: {
  name: string;
  you?: boolean;
  size?: "sm" | "md" | "lg";
}) {
  return (
    <span className={`who-mark who-${size}${you ? " is-you" : ""}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export type RecordKind = "lead" | "customer" | "policy" | "task";
const KIND_WORD: Record<RecordKind, string> = {
  lead: "Lead",
  customer: "Customer",
  policy: "Policy",
  task: "Task",
};

/**
 * What kind of record this is: the one place the identity hues appear. Never a status; a lead
 * that is cold is still blue, and says "cold" in words elsewhere.
 */
export function RecordMark({ kind, children }: { kind: RecordKind; children?: ReactNode }) {
  return <span className={`id-mark id-${kind}`}>{children ?? KIND_WORD[kind]}</span>;
}

/** A square of the record's hue, for navigation and lane heads, always beside its name. */
export function KindDot({ kind }: { kind: RecordKind }) {
  return <span className={`id-dot id-${kind}`} aria-hidden="true" />;
}

/* ---- Numbers ------------------------------------------------------------------------------- */

/**
 * Seven little bars, oldest first, today last and darker. Shape only: the tile's text carries the
 * number, so the spark is hidden from assistive tech.
 */
export function Spark({ values }: { values: number[] }) {
  const max = Math.max(0, ...values);
  return (
    <span className="spark" aria-hidden="true">
      {values.map((v, i) => (
        <span
          key={i}
          className={i === values.length - 1 ? "is-now" : undefined}
          style={{ height: `${max ? Math.max(6, (v / max) * 100) : 6}%` }}
        />
      ))}
    </span>
  );
}

/** A day's number with its last seven days, and a worded comparison under it. */
export function KpiTile({
  label,
  value,
  sub,
  spark,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
  spark?: number[];
}) {
  return (
    <section className="kpi" aria-label={label}>
      <span className="eyebrow">{label}</span>
      <div className="kpi-row">
        <span className="kpi-v mono">{value}</span>
        {spark && <Spark values={spark} />}
      </div>
      <div className="kpi-s">{sub}</div>
    </section>
  );
}

const PACE_WORD: Record<Pace, string> = {
  ahead: "Ahead of pace",
  on: "On pace",
  behind: "Behind pace",
  done: "Goal met",
  missed: "Goal missed",
};

export function PaceMark({ pace }: { pace: Pace }) {
  const tone =
    pace === "done" || pace === "ahead"
      ? "accent"
      : pace === "on"
        ? "ink"
        : pace === "behind"
          ? "ochre"
          : "brick";
  return <span className={`mark mark-${tone}`}>{PACE_WORD[pace]}</span>;
}

/** A goal bar with a tick where the pace says it should be by now. */
export function PaceBar({
  value,
  goal,
  elapsed,
  label,
  onDark,
}: {
  value: number;
  goal: number;
  elapsed: number;
  label: string;
  onDark?: boolean;
}) {
  const share = goal > 0 ? Math.min(1, value / goal) : 0;
  return (
    <div
      className={`pace${onDark ? " on-dark" : ""}`}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={goal}
      aria-valuenow={value}
    >
      <span className="pace-fill" style={{ width: `${share * 100}%` }} />
      <span className="pace-tick" style={{ left: `${Math.min(1, elapsed) * 100}%` }} />
    </div>
  );
}

/* ---- Card states --------------------------------------------------------------------------- */

/**
 * A card's body while its data is on the way, failed, or empty, so one slow query never blanks
 * the page and every card says what it is waiting for.
 */
export function CardBody<T>({
  query,
  rows = 3,
  empty,
  isEmpty,
  children,
}: {
  query: { data: T | undefined; isError: boolean; error: unknown; refetch: () => unknown };
  rows?: number;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isError && query.data === undefined) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.data === undefined) {
    return (
      <div className="card-skel" aria-busy="true">
        {Array.from({ length: rows }, (_, i) => (
          <span key={i} className="skel" />
        ))}
      </div>
    );
  }
  if (empty && isEmpty?.(query.data)) return <div className="card-empty">{empty}</div>;
  return <>{children(query.data)}</>;
}

/* ---- May this call be made? ---------------------------------------------------------------- */

/**
 * Before a dial: is the number on the do-not-call list, is it inside 8 AM to 9 PM where the
 * person is, may this agent sell there, is there consent on file, does it want Spanish. Each is
 * a word; the stop ones are brick, the cautions ochre.
 */
export function EligibilityFlags({
  state,
  language,
  consentType,
  doNotCall,
  licensedStates,
  now,
  compact,
}: {
  state: string | null;
  language?: Language;
  consentType?: ConsentType | null;
  doNotCall?: boolean;
  licensedStates?: string[];
  now: number;
  compact?: boolean;
}) {
  const flags: { tone: "brick" | "ochre" | "mute"; text: string; title: string }[] = [];
  if (doNotCall) {
    flags.push({ tone: "brick", text: "DNC", title: "On the do-not-call list: do not dial" });
  }
  const local = localCallWindow(state, new Date(now));
  if (local && !local.open) {
    flags.push({
      tone: "brick",
      text: `${local.time} there`,
      title: "Outside 8 AM to 9 PM where they are: wait until morning",
    });
  } else if (local && local.minutesLeft <= 45) {
    flags.push({
      tone: "ochre",
      text: `${local.time} there`,
      title: `${local.minutesLeft} minutes left to call today where they are`,
    });
  } else if (local && !compact) {
    flags.push({ tone: "mute", text: `${local.time} there`, title: "Their local time" });
  }
  if (state && licensedStates && licensedStates.length && !licensedStates.includes(state)) {
    flags.push({
      tone: "ochre",
      text: `Not licensed in ${state}`,
      title: `You are not appointed in ${state}: hand this to someone who is`,
    });
  }
  if (consentType === "none") {
    flags.push({
      tone: "ochre",
      text: "No consent",
      title: "No consent on file: a manual call only, and ask before anything else",
    });
  }
  if (language === "es") {
    flags.push({ tone: "mute", text: languageLabel("es"), title: "Prefers Spanish" });
  }
  if (!flags.length) return null;
  return (
    <span className="elig">
      {flags.map((f) => (
        <span key={f.text} className={`mark mark-${f.tone}`} title={f.title}>
          {f.text}
        </span>
      ))}
    </span>
  );
}

/* ---- Streak -------------------------------------------------------------------------------- */

/**
 * Twelve weeks, oldest first: a filled square for a week with a sale, a hollow one without, and
 * this week outlined until it has one. The count is in words beside it.
 */
export function StreakStrip({
  weeks,
  thisWeekSold,
}: {
  weeks: { start: string; premium: number }[];
  thisWeekSold: boolean;
}) {
  return (
    <span className="streak" role="img" aria-label={streakLabel(weeks)}>
      {weeks.map((w, i) => {
        const now = i === weeks.length - 1;
        const cls = w.premium > 0 ? "is-sold" : now && !thisWeekSold ? "is-open" : "";
        return <span key={w.start} className={cls} />;
      })}
    </span>
  );
}

function streakLabel(weeks: { premium: number }[]): string {
  const sold = weeks.filter((w) => w.premium > 0).length;
  return `${sold} of the last ${weeks.length} weeks with a sale`;
}

/* ---- Keys ---------------------------------------------------------------------------------- */

export function Keycap({ children }: { children: ReactNode }) {
  return <kbd className="keycap">{children}</kbd>;
}
